import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Switch, IconButton, Button, Tooltip, Zoom, Chip, useTheme,
  Dialog, DialogTitle, DialogActions, DialogContent, InputAdornment, OutlinedInput,
  Select, MenuItem, FormControl, TextField, Divider, CircularProgress,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import LockIcon from '@mui/icons-material/Lock';
import HistoryIcon from '@mui/icons-material/History';
import AssessmentIcon from '@mui/icons-material/Assessment';
import TuneIcon from '@mui/icons-material/Tune';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useApp } from '../context/AppContext';
import { Flag } from '../api/flags';
import { projectsApi } from '../api/projects';

/** Collect all dependency flag keys in the tree (deduped, circular-safe) */
function collectAllDepKeys(flagKey: string, allFlags: Flag[], visited = new Set<string>()): string[] {
  if (visited.has(flagKey)) return [];
  visited.add(flagKey);
  const flag = allFlags.find(f => f.key === flagKey);
  if (!flag) return [];
  const result: string[] = [];
  for (const dep of flag.dependencies) {
    result.push(dep);
    result.push(...collectAllDepKeys(dep, allFlags, visited));
  }
  return [...new Set(result)];
}

const ENV_COLORS: Record<string, string> = {
  dev: '#00C7BE',
  staging: '#FF9F0A',
  prod: '#FF3B30',
};

function envColor(key: string): string {
  return ENV_COLORS[key] ?? '#8E8E93';
}

const DEFAULT_KEYS = new Set(['app-enable', 'app-maintenance-mode']);

// Recursively render the dependency tree
function DepTree({
  flagKey,
  allFlags,
  envKey,
  depth = 0,
  visited = new Set<string>(),
  isDark,
  borderColor,
}: {
  flagKey: string;
  allFlags: Flag[];
  envKey: string;
  depth?: number;
  visited?: Set<string>;
  isDark: boolean;
  borderColor: string;
}) {
  const flag = allFlags.find(f => f.key === flagKey);
  const isCircular = visited.has(flagKey);
  const nextVisited = new Set(visited).add(flagKey);

  const effectivelyOn = flag ? (flag.environments[envKey] ?? false) : false;

  return (
    <Box sx={{ ml: depth === 0 ? 0 : 3, position: 'relative' }}>
      {depth > 0 && (
        <Box sx={{
          position: 'absolute', left: -20, top: 0, bottom: '50%',
          borderLeft: `2px solid ${borderColor}`, borderBottom: `2px solid ${borderColor}`,
          width: 16, borderBottomLeftRadius: 4,
        }} />
      )}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.5, py: 1, px: 1.5,
        borderRadius: 2, mb: 0.5,
        bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
        border: `1px solid ${borderColor}`,
      }}>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {!flag && (
              <Typography sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 14, fontStyle: 'italic' }}>
                {flagKey} <span style={{ fontSize: 11 }}>(not found)</span>
              </Typography>
            )}
            {flag && (
              <Typography sx={{ fontWeight: 600, color: 'text.primary', fontSize: 14 }}>
                {flag.name}
              </Typography>
            )}
            {isCircular && (
              <Chip label="circular" size="small" sx={{ fontSize: 10, height: 18, bgcolor: 'rgba(255,59,48,0.1)', color: 'primary.main' }} />
            )}
          </Box>
          <Typography sx={{ fontFamily: 'monospace', fontSize: 11, color: 'text.secondary', mt: 0.25 }}>
            {flagKey}
          </Typography>
        </Box>

        {/* Status badge — shown on every node when env is selected */}
        {envKey && flag && (
          <Box sx={{
            px: 1.5, py: 0.5, borderRadius: 1.5, minWidth: 44, textAlign: 'center',
            bgcolor: effectivelyOn
              ? (isDark ? 'rgba(0,199,190,0.15)' : 'rgba(0,199,190,0.12)')
              : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'),
            border: `1px solid ${effectivelyOn ? 'rgba(0,199,190,0.3)' : borderColor}`,
          }}>
            <Typography sx={{
              fontSize: 11, fontWeight: 700,
              color: effectivelyOn ? '#00C7BE' : 'text.secondary',
            }}>
              {effectivelyOn ? 'ON' : 'OFF'}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Children */}
      {!isCircular && flag && flag.dependencies.length > 0 && (
        <Box sx={{ ml: 2, pl: 0.5, borderLeft: `2px solid ${borderColor}`, mt: -0.5, mb: 0.5 }}>
          {flag.dependencies.map(dep => (
            <DepTree
              key={dep}
              flagKey={dep}
              allFlags={allFlags}
              envKey={envKey}
              depth={depth + 1}
              visited={nextVisited}
              isDark={isDark}
              borderColor={borderColor}
            />
          ))}
        </Box>
      )}

      {flag && flag.dependencies.length === 0 && (
        <Box sx={{ ml: 4, mb: 0.5 }}>
          <Typography sx={{ fontSize: 11, color: 'text.secondary', fontStyle: 'italic' }}>
            no dependencies
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default function Dashboard() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const { projects, flags, currentProject, loading, loadProjects, loadFlags, selectProject, updateFlag } = useApp();

  void projects;
  void loading;

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [apiKeyVisible, setApiKeyVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const [depFlag, setDepFlag] = useState<Flag | null>(null);
  const [depEnv, setDepEnv] = useState('');

  // Env management
  const [envAdding, setEnvAdding] = useState(false);
  const [newEnvKey, setNewEnvKey] = useState('');
  const [newEnvName, setNewEnvName] = useState('');
  const [newEnvDesc, setNewEnvDesc] = useState('');
  const [envSaving, setEnvSaving] = useState(false);
  const [envError, setEnvError] = useState('');
  const [deletingEnvKey, setDeletingEnvKey] = useState<string | null>(null);
  const [envDeleting, setEnvDeleting] = useState(false);

  const handleAddEnv = async () => {
    setEnvError('');
    if (!newEnvKey.trim()) { setEnvError('Key is required'); return; }
    if (!newEnvName.trim()) { setEnvError('Name is required'); return; }
    if (!/^[a-z0-9-_]+$/.test(newEnvKey.trim())) { setEnvError('Key must be lowercase letters, numbers, hyphens or underscores'); return; }
    if (!projectId) return;
    setEnvSaving(true);
    try {
      await projectsApi.createEnvironment(projectId, { key: newEnvKey.trim(), name: newEnvName.trim(), description: newEnvDesc.trim() });
      await loadProjects();
      setEnvAdding(false);
      setNewEnvKey(''); setNewEnvName(''); setNewEnvDesc(''); setEnvError('');
    } catch (e: unknown) {
      setEnvError(e instanceof Error ? e.message : 'Failed to add environment');
    } finally { setEnvSaving(false); }
  };

  const handleDeleteEnv = async () => {
    if (!deletingEnvKey || !projectId) return;
    setEnvDeleting(true);
    try {
      await projectsApi.deleteEnvironment(projectId, deletingEnvKey);
      await loadProjects();
      setDeletingEnvKey(null);
    } catch (e: unknown) {
      setEnvError(e instanceof Error ? e.message : 'Failed to delete environment');
    } finally { setEnvDeleting(false); }
  };

  const handleCopyApiKey = () => {
    if (!currentProject?.apiKey) return;
    navigator.clipboard.writeText(currentProject.apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const borderColor = isDark ? '#2C2C2E' : '#E5E5EA';
  const keyBg = isDark ? '#121212' : '#F2F2F7';
  const hoverBg = isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)';
  const defaultRowBg = isDark ? 'rgba(0,199,190,0.03)' : 'rgba(0,199,190,0.04)';

  useEffect(() => { loadProjects(); }, [loadProjects]);

  useEffect(() => {
    if (projectId) { selectProject(projectId); loadFlags(projectId); }
  }, [projectId, selectProject, loadFlags]);

  if (!projectId) {
    return (
      <Box sx={{ pt: 10, textAlign: 'center' }}>
        <Box sx={{ display: 'inline-flex', p: 2, bgcolor: isDark ? 'rgba(255,59,48,0.1)' : 'rgba(255,59,48,0.06)', borderRadius: 3, mb: 3 }}>
          <AssessmentIcon sx={{ fontSize: 40, color: 'primary.main' }} />
        </Box>
        <Typography variant="h5" fontWeight={800} color="text.primary" mb={1}>No project selected</Typography>
        <Typography color="text.secondary">Select or create a project from the top navigation.</Typography>
      </Box>
    );
  }

  const envKeys = currentProject?.environments?.slice().sort((a, b) => a.order - b.order).map(e => e.key) ?? [];
  const defaultFlags = flags.filter(f => DEFAULT_KEYS.has(f.key));
  const customFlags = flags.filter(f => !DEFAULT_KEYS.has(f.key));
  const orderedFlags = [...defaultFlags, ...customFlags];
  const enabledCount = flags.filter(f => Object.values(f.environments).some(Boolean)).length;

  const handleEnvToggle = async (flagId: string, envKey: string, current: boolean) => {
    const flag = flags.find(f => f.flagId === flagId);
    if (!flag) return;
    await updateFlag(flagId, { environments: { ...flag.environments, [envKey]: !current } });
  };

  return (
    <Box sx={{ p: { xs: 2, md: 5 }, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 4, flexWrap: 'wrap', gap: 3 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontWeight: 800, color: 'text.primary', fontSize: { xs: 24, md: 30 }, letterSpacing: '-0.02em' }}>
              Feature Flags
            </Typography>
            <Chip label={currentProject?.name ?? ''} size="small" sx={{ bgcolor: isDark ? 'rgba(0,199,190,0.1)' : 'rgba(0,199,190,0.1)', color: 'secondary.main', fontWeight: 700, fontSize: 11, border: '1px solid rgba(0,199,190,0.25)' }} />
          </Box>
          <Typography sx={{ color: 'text.secondary', maxWidth: 520, lineHeight: 1.6 }}>
            Control features across environments.{' '}
            <Box component="span" sx={{ color: 'secondary.main', fontWeight: 600 }}>{enabledCount} of {flags.length}</Box>{' '}
            flags have at least one environment enabled.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button variant="outlined" startIcon={<TuneIcon />}
            onClick={() => { setApiKeyVisible(false); setSettingsOpen(true); }}
            sx={{ py: 1.5, px: 2.5, fontWeight: 700, borderRadius: 2, whiteSpace: 'nowrap', borderColor, color: 'text.secondary', '&:hover': { borderColor: 'secondary.main', color: 'secondary.main', bgcolor: 'rgba(0,199,190,0.06)' } }}>
            Project Settings
          </Button>
          <Button variant="outlined" startIcon={<AssessmentIcon />}
            onClick={() => navigate(`/projects/${projectId}/audit`)}
            sx={{ py: 1.5, px: 2.5, fontWeight: 700, borderRadius: 2, whiteSpace: 'nowrap', borderColor, color: 'text.secondary', '&:hover': { borderColor: '#0A84FF', color: '#0A84FF', bgcolor: 'rgba(10,132,255,0.06)' } }}>
            Project Audit
          </Button>
          <Button variant="contained" color="primary" startIcon={<AddIcon />}
            onClick={() => navigate(`/projects/${projectId}/flags/new`)}
            sx={{ py: 1.5, px: 3, fontWeight: 700, borderRadius: 2, whiteSpace: 'nowrap' }}>
            New Flag
          </Button>
        </Box>
      </Box>

      {/* Legend */}
      <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        {envKeys.map(k => (
          <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: envColor(k) }} />
            <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 500 }}>
              {currentProject?.environments.find(e => e.key === k)?.name ?? k}
            </Typography>
          </Box>
        ))}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, ml: 1 }}>
          <LockIcon sx={{ fontSize: 12, color: 'secondary.main' }} />
          <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 500 }}>System default flag</Typography>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${borderColor}`, borderRadius: 4, overflow: 'hidden', bgcolor: 'background.paper' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ pl: 4, fontWeight: 700 }}>Feature Details</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Settings</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Audit</TableCell>
              {envKeys.map(k => (
                <TableCell key={k} align="center">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: envColor(k) }} />
                    {currentProject?.environments.find(e => e.key === k)?.name ?? k}
                  </Box>
                </TableCell>
              ))}
              <TableCell align="center" sx={{ fontWeight: 700 }}>Dependencies</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orderedFlags.length === 0 && (
              <TableRow>
                <TableCell colSpan={4 + envKeys.length} align="center" sx={{ py: 8 }}>
                  <Typography sx={{ color: 'text.secondary', mb: 2 }}>No feature flags yet.</Typography>
                  <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate(`/projects/${projectId}/flags/new`)} sx={{ borderRadius: 2, fontWeight: 700 }}>
                    Create your first flag
                  </Button>
                </TableCell>
              </TableRow>
            )}
            {orderedFlags.map((flag, idx) => {
              const isDefault = DEFAULT_KEYS.has(flag.key);
              const isLastDefault = isDefault && idx === defaultFlags.length - 1;
              const hasDeps = flag.dependencies.length > 0;
              return (
                <TableRow key={flag.flagId} hover sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                  transition: 'background-color 0.15s',
                  bgcolor: isDefault ? defaultRowBg : 'transparent',
                  '&:hover': { bgcolor: isDefault ? (isDark ? 'rgba(0,199,190,0.06)' : 'rgba(0,199,190,0.07)') : hoverBg },
                  ...(isLastDefault && customFlags.length > 0 ? { '& td': { borderBottom: `2px solid ${isDark ? '#2C2C2E' : '#D1D1D6'} !important` } } : {}),
                }}>
                  {/* Feature details */}
                  <TableCell sx={{ pl: 4 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {isDefault && (
                          <Tooltip title="System default flag — present in every project" TransitionComponent={Zoom}>
                            <LockIcon sx={{ fontSize: 14, color: 'secondary.main', flexShrink: 0 }} />
                          </Tooltip>
                        )}
                        <Typography sx={{ fontWeight: 600, color: 'text.primary', fontSize: 15 }}>{flag.name}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography sx={{ color: 'text.secondary', fontFamily: 'monospace', fontSize: 12, bgcolor: keyBg, px: 1, py: 0.4, borderRadius: 1, border: `1px solid ${borderColor}` }}>
                          {flag.key}
                        </Typography>
                        {hasDeps && flag.dependencies.map(dep => (
                          <Chip key={dep} label={`depends on: ${dep}`} size="small" sx={{ fontSize: 10, height: 20, bgcolor: 'rgba(255,159,10,0.1)', color: '#FF9F0A', border: '1px solid rgba(255,159,10,0.2)', fontFamily: 'monospace' }} />
                        ))}
                      </Box>
                    </Box>
                  </TableCell>

                  {/* Settings */}
                  <TableCell align="center">
                    <Tooltip title="Edit Feature Settings" TransitionComponent={Zoom}>
                      <IconButton onClick={() => navigate(`/projects/${projectId}/flags/${flag.flagId}`)} sx={{ color: 'text.secondary', bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)', '&:hover': { color: 'primary.main', bgcolor: 'rgba(255,59,48,0.1)' }, transition: 'all 0.2s', borderRadius: 2 }}>
                        <SettingsIcon sx={{ fontSize: 19 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>

                  {/* Audit */}
                  <TableCell align="center">
                    <Tooltip title="View audit log" TransitionComponent={Zoom}>
                      <IconButton onClick={() => navigate(`/projects/${projectId}/audit`)} sx={{ color: 'text.secondary', bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)', '&:hover': { color: '#0A84FF', bgcolor: 'rgba(10,132,255,0.1)' }, transition: 'all 0.2s', borderRadius: 2 }}>
                        <HistoryIcon sx={{ fontSize: 19 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>

                  {/* Env toggles */}
                  {envKeys.map(k => (
                    <TableCell key={k} align="center">
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                        <Switch checked={flag.environments[k] ?? false} onChange={() => handleEnvToggle(flag.flagId, k, flag.environments[k] ?? false)} color="secondary" size="small" />
                        <Typography sx={{ fontSize: 10, fontWeight: 600, color: flag.environments[k] ? envColor(k) : 'text.secondary' }}>
                          {flag.environments[k] ? 'ON' : 'OFF'}
                        </Typography>
                      </Box>
                    </TableCell>
                  ))}

                  {/* Dependencies */}
                  <TableCell align="center">
                    <Tooltip title="View dependency tree" TransitionComponent={Zoom}>
                      <IconButton
                        onClick={() => { setDepFlag(flag); setDepEnv(''); }}
                        size="small"
                        sx={{
                          color: hasDeps ? '#FF9F0A' : 'text.secondary',
                          bgcolor: hasDeps
                            ? (isDark ? 'rgba(255,159,10,0.08)' : 'rgba(255,159,10,0.06)')
                            : (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'),
                          '&:hover': { color: '#FF9F0A', bgcolor: 'rgba(255,159,10,0.15)' },
                          borderRadius: 2,
                        }}
                      >
                        <AccountTreeIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {orderedFlags.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2.5 }}>
          <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
            <Box component="span" sx={{ color: 'secondary.main', fontWeight: 600 }}>{defaultFlags.length}</Box> system default
            {customFlags.length > 0 && <>{' · '}<Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>{customFlags.length}</Box> custom</>}
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>{orderedFlags.length} flag{orderedFlags.length !== 1 ? 's' : ''} total</Typography>
        </Box>
      )}

      {/* Dependency Tree Dialog */}
      <Dialog
        open={Boolean(depFlag)}
        onClose={() => setDepFlag(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <AccountTreeIcon sx={{ color: '#FF9F0A' }} />
            Dependency Tree
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {/* Env selector — static label above select */}
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>
            Show toggle status for environment
          </Typography>
          <FormControl fullWidth sx={{ mb: 3 }}>
            <Select
              value={depEnv}
              displayEmpty
              onChange={e => setDepEnv(e.target.value)}
            >
              <MenuItem value=""><em style={{ fontStyle: 'italic', opacity: 0.6 }}>None — hide status</em></MenuItem>
              {envKeys.map(k => (
                <MenuItem key={k} value={k}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: envColor(k), flexShrink: 0 }} />
                    <Typography fontWeight={600}>{currentProject?.environments.find(e => e.key === k)?.name ?? k}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{k}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Tree */}
          {depFlag && (
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {depFlag.name}
              </Typography>
              <DepTree
                flagKey={depFlag.key}
                allFlags={flags}
                envKey={depEnv}
                isDark={isDark}
                borderColor={borderColor}
              />
            </Box>
          )}

          {depFlag && depFlag.dependencies.length === 0 && (
            <Box sx={{ mt: 1, p: 2, bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)', borderRadius: 2, border: `1px solid ${borderColor}`, textAlign: 'center' }}>
              <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                This flag has no dependencies.
              </Typography>
            </Box>
          )}

          {/* Status banner — only when env selected and flag has deps */}
          {depEnv && depFlag && depFlag.dependencies.length > 0 && (() => {
            const allDepKeys = collectAllDepKeys(depFlag.key, flags);
            const allOn = allDepKeys.every(k => {
              const f = flags.find(fl => fl.key === k);
              return f ? (f.environments[depEnv] ?? false) : false;
            });
            return (
              <Box sx={{
                mt: 2.5, display: 'flex', alignItems: 'center', gap: 1.5, p: 1.75,
                borderRadius: 2,
                bgcolor: allOn
                  ? (isDark ? 'rgba(52,199,89,0.08)' : 'rgba(52,199,89,0.07)')
                  : (isDark ? 'rgba(255,159,10,0.08)' : 'rgba(255,159,10,0.07)'),
                border: `1px solid ${allOn ? 'rgba(52,199,89,0.3)' : 'rgba(255,159,10,0.3)'}`,
              }}>
                {allOn
                  ? <CheckCircleIcon sx={{ color: '#34C759', fontSize: 20, flexShrink: 0 }} />
                  : <WarningAmberIcon sx={{ color: '#FF9F0A', fontSize: 20, flexShrink: 0 }} />
                }
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 13, color: allOn ? '#34C759' : '#FF9F0A' }}>
                    {allOn ? 'No conflict' : 'Dependencies not fully enabled'}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.25 }}>
                    {allOn
                      ? 'All dependencies are ON in this environment.'
                      : `One or more dependencies are OFF or have masterActive disabled in ${currentProject?.environments.find(e => e.key === depEnv)?.name ?? depEnv}.`
                    }
                  </Typography>
                </Box>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setDepFlag(null)} sx={{ fontWeight: 600 }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Project Settings Dialog */}
      <Dialog open={settingsOpen} onClose={() => { setSettingsOpen(false); setEnvAdding(false); setEnvError(''); }} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800, pb: 0 }}>Project Settings</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75, fontWeight: 600 }}>Project Name</Typography>
          <Typography sx={{ fontWeight: 700, mb: 3, fontSize: 16 }}>{currentProject?.name}</Typography>

          <Divider sx={{ mb: 3 }} />

          {/* Environments */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>Environments</Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={() => { setEnvAdding(true); setEnvError(''); }}
              sx={{ fontWeight: 700, fontSize: 12, color: 'secondary.main', '&:hover': { bgcolor: 'rgba(0,199,190,0.08)' } }}>
              Add
            </Button>
          </Box>

          {/* Env list */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: envAdding ? 2 : 3 }}>
            {(currentProject?.environments ?? []).slice().sort((a, b) => a.order - b.order).map(env => (
              <Box key={env.key} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1, borderRadius: 2, border: `1px solid ${borderColor}`, bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: envColor(env.key), flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{env.name}</Typography>
                  <Typography sx={{ fontFamily: 'monospace', fontSize: 11, color: 'text.secondary' }}>{env.key}</Typography>
                </Box>
                {env.description && (
                  <Typography sx={{ fontSize: 12, color: 'text.secondary', mr: 1, display: { xs: 'none', sm: 'block' } }}>{env.description}</Typography>
                )}
                <Tooltip title={`Delete ${env.name}`} TransitionComponent={Zoom}>
                  <IconButton size="small" onClick={() => setDeletingEnvKey(env.key)}
                    sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main', bgcolor: 'rgba(255,59,48,0.08)' }, borderRadius: 1.5 }}>
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Box>

          {/* Add env inline form */}
          {envAdding && (
            <Box sx={{ p: 2, borderRadius: 2, border: `1px solid rgba(0,199,190,0.3)`, bgcolor: isDark ? 'rgba(0,199,190,0.04)' : 'rgba(0,199,190,0.03)', mb: 3 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 1.5, color: 'secondary.main' }}>New Environment</Typography>
              <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
                <TextField size="small" label="Key" placeholder="e.g. qa" value={newEnvKey}
                  onChange={e => { setNewEnvKey(e.target.value.toLowerCase().replace(/[^a-z0-9-_]/g, '')); setEnvError(''); }}
                  sx={{ flex: '1 1 100px' }} InputProps={{ sx: { fontFamily: 'monospace', borderRadius: 2 } }} />
                <TextField size="small" label="Name" placeholder="e.g. QA" value={newEnvName}
                  onChange={e => { setNewEnvName(e.target.value); setEnvError(''); }}
                  sx={{ flex: '2 1 140px' }} InputProps={{ sx: { borderRadius: 2 } }} />
              </Box>
              <TextField size="small" fullWidth label="Description (optional)" value={newEnvDesc}
                onChange={e => setNewEnvDesc(e.target.value)}
                sx={{ mb: 1.5 }} InputProps={{ sx: { borderRadius: 2 } }} />
              {envError && <Typography sx={{ fontSize: 12, color: 'primary.main', mb: 1 }}>{envError}</Typography>}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" variant="contained" color="secondary" onClick={handleAddEnv} disabled={envSaving}
                  sx={{ fontWeight: 700, borderRadius: 2 }}>
                  {envSaving ? <CircularProgress size={14} color="inherit" /> : 'Add Environment'}
                </Button>
                <Button size="small" onClick={() => { setEnvAdding(false); setNewEnvKey(''); setNewEnvName(''); setNewEnvDesc(''); setEnvError(''); }}
                  sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  Cancel
                </Button>
              </Box>
            </Box>
          )}

          <Divider sx={{ mb: 3 }} />

          {/* API Key */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>API Key</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, lineHeight: 1.5 }}>
            Use this key in the <code>x-api-key</code> header to access the Consumer API (read-only flag evaluation).
          </Typography>
          <OutlinedInput
            fullWidth readOnly
            value={apiKeyVisible ? (currentProject?.apiKey ?? '') : '•'.repeat(36)}
            sx={{ fontFamily: 'monospace', fontSize: 13, borderRadius: 2, letterSpacing: apiKeyVisible ? 0.5 : 2, '& .MuiOutlinedInput-notchedOutline': { borderColor } }}
            endAdornment={
              <InputAdornment position="end">
                <Tooltip title={apiKeyVisible ? 'Hide' : 'Show'} TransitionComponent={Zoom}>
                  <IconButton onClick={() => setApiKeyVisible(v => !v)} edge="end" size="small" sx={{ mr: 0.5 }}>
                    {apiKeyVisible ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'} TransitionComponent={Zoom}>
                  <IconButton onClick={handleCopyApiKey} edge="end" size="small" sx={{ color: copied ? 'secondary.main' : 'text.secondary' }}>
                    {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            }
          />
          <Box sx={{ mt: 3, p: 2, bgcolor: isDark ? 'rgba(0,199,190,0.05)' : 'rgba(0,199,190,0.06)', borderRadius: 2, border: `1px solid rgba(0,199,190,0.2)` }}>
            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary', display: 'block', whiteSpace: 'pre' }}>{
`fetch('https://api.haki.kotahirau.com/v1/flags?env=prod', {
  headers: { 'x-api-key': '${apiKeyVisible ? (currentProject?.apiKey ?? '<key>') : '<your-api-key>'}' }
}).then(r => r.json())`
            }</Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => { setSettingsOpen(false); setEnvAdding(false); setEnvError(''); }} sx={{ fontWeight: 600 }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete env confirm dialog */}
      <Dialog open={Boolean(deletingEnvKey)} onClose={() => setDeletingEnvKey(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>Delete Environment?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>
            This will remove <strong>{currentProject?.environments.find(e => e.key === deletingEnvKey)?.name ?? deletingEnvKey}</strong> from all flags in this project. This cannot be undone.
          </Typography>
          {envError && <Typography sx={{ fontSize: 13, color: 'primary.main', mt: 1.5 }}>{envError}</Typography>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => { setDeletingEnvKey(null); setEnvError(''); }} sx={{ fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteEnv} disabled={envDeleting} sx={{ fontWeight: 700, borderRadius: 2 }}>
            {envDeleting ? <CircularProgress size={14} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
